import NeptuneElement from '../../../NeptuneElement.js';

class KebabMenuItem extends NeptuneElement {

    templateMarkup = `
        <li>
            <a>
                <neptune-avatar size="sm" hidden></neptune-avatar>
                <i class="fa-regular"></i>
                <span class="font-size-md"></span>
                <i class="fa-solid"></i>
            </a>
        </li>
    `;

    componentCssUrl = '/src/components/chrome/kebabMenu/KebabMenuItem/kebab-menu-item.css';

    async connectedCallback() {
        await super.connectedCallback();
        this.processInitialAttributesAfterRender(KebabMenuItem.observedAttributes);
    }

    async disconnectedCallback() {
        await super.disconnectedCallback();
    }

    static get observedAttributes() {
        return ['label', 'route', 'icon', 'active', 'expanded', 'submenu', 'collapsed', 'avatarText'];
    }

    setEventListeners() {
        this.linkElement = this.shadowRoot.querySelector('a');
        this.handleClickBound = this.#handleClick.bind(this);
        
        this.linkElement?.addEventListener('click', this.handleClickBound);
    }

    removeEventListeners() {
        this.linkElement?.removeEventListener('click', this.handleClickBound);
        
        this.linkElement = null;
        this.handleClickBound = null;
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (!this.isRendered) {
            return;
        }
        this.processAttribute(name, oldValue, newValue);
    }
    
    /**
     * Processes a single attribute change and updates the item's rendered state.
     * Handles label, route, icon, avatarText, collapsed, and the shared submenu/active/expanded state case.
     * @param {string} name - The name of the changed attribute.
     * @param {string|null} oldValue - The previous value of the attribute.
     * @param {string|null} newValue - The new value of the attribute.
     * @throws {Error} When required shadow DOM elements (link or label) are not found.
     * @returns {void}
     */
    processAttribute(name, oldValue, newValue) {
        const link = this.shadowRoot?.querySelector('a');
        const label = this.shadowRoot?.querySelector('span.font-size-md');
        const avatar = this.shadowRoot?.querySelector('neptune-avatar');
        let iconLeft = this.shadowRoot?.querySelector('i:first-of-type');
        const iconRight = this.shadowRoot?.querySelector('i:last-of-type');

        if (!link || !label) {
            throw new Error(`KebabMenuItem: Required elements not found in shadow DOM. Missing: ${!link ? 'link' : ''} ${!label ? 'label' : ''}`);
        }
        switch (name) {
            case 'label': {
                const labelText = newValue || '';
                label.textContent = labelText;
                const isCollapsed = this.hasAttribute('collapsed');
                if (isCollapsed) {
                    link.title = labelText;
                }
                break;
            }
            case 'route': {
                link.href = newValue || '#';
                break;
            }
            case 'icon': {
                if (!iconLeft) {
                    iconLeft = document.createElement('i');
                    iconLeft.className = 'fa-regular';
                    link.insertBefore(iconLeft, link.querySelector('span.font-size-md'));
                }
                if (newValue) {
                    iconLeft.className = `fa-solid ${newValue}`;
                    iconLeft.removeAttribute('hidden');
                } else {
                    iconLeft.className = 'fa-regular';
                    iconLeft.setAttribute('hidden', '');
                }
                break;
            }
            case 'avatarText': {
                if (!avatar) {break;}

                if (newValue) {
                    avatar.setAttribute('initials', newValue);
                    avatar.removeAttribute('hidden');
                    if (iconLeft) {iconLeft.setAttribute('hidden', '');}
                } else {
                    avatar.removeAttribute('initials');
                    avatar.setAttribute('hidden', '');
                }
                break;
            }
            case 'collapsed': {
                const isCollapsed = this.hasAttribute('collapsed');
                const labelText = this.getAttribute('label') || '';
                link.title = isCollapsed ? labelText : '';
                break;
            }
            case 'submenu':
            case 'active':
            case 'expanded': {
                const isActive = this.hasAttribute('active');
                const isExpanded = this.hasAttribute('expanded');
                const hasSubmenu = this.hasAttribute('submenu');

                if (hasSubmenu) {
                    iconRight.className = `fa-solid ${isExpanded ? 'fa-chevron-left' : 'fa-chevron-right'}`;
                    iconRight.removeAttribute('hidden');
                } else {
                    iconRight.setAttribute('hidden', '');
                }
                let state = 'default';
                if(isActive) {
                    state = 'active';
                } else if(isExpanded) {
                    state = 'expanded';
                }
                this.setAttribute('state', state);
                break;
            }
            default: {
                break;
            }
        }
    }
    
    /**
     * Handles a click on the internal anchor element.
     * Parent items (with `submenu`): toggle expanded state and emit `neptune-kebab-menu-item-toggle`.
     * @param {MouseEvent} event - The click event.
     * @returns {void}
     */
    #handleClick(event) {
        const hasSubmenu = this.hasAttribute('submenu');

        if (hasSubmenu) {
            event.preventDefault();
            const isExpanded = this.hasAttribute('expanded');
            this.toggleAttribute('expanded', !isExpanded);

            this.dispatchEvent(new CustomEvent('neptune-kebab-menu-item-toggle', {
                bubbles: true,
                composed: true,
                detail: {
                    expanded: !isExpanded,
                    label: this.getAttribute('label')
                }
            }));
        } else {
            event.preventDefault();
            this.dispatchEvent(new CustomEvent('neptune-kebab-menu-item-click', {
                bubbles: true,
                composed: true,
                detail: {
                    id: this.getAttribute('data-item-id'),
                    route: this.getAttribute('route'),
                    label: this.getAttribute('label')
                }
            }));
        }
    }
}

if (!customElements.get('neptune-kebab-menu-item')) {
    customElements.define('neptune-kebab-menu-item', KebabMenuItem);
}

export default KebabMenuItem;